import { useEffect } from "react";
import { useDispatch, useStore } from "react-redux";
import { applyRelocationSelection } from "../Drawers/RoutesDrawer/utils/applyPinSelection";

const STROKE = "#0DB14B"; // verde do sistema (Stone) — borda e preenchimento da área
const FILL_OPACITY = 0.15;
const SNAP_PX = 18;
const FILL_Z_INDEX = 10006;
const LINE_Z_INDEX = 10007;
const SNAP_Z_INDEX = 10008;

// Converte um LatLng para pixel dentro do container do mapa (para medir
// distância em pixels — usado pelo "snap" ao primeiro ponto do laço).
const toContainerPixel = (map, latLng) => {
  const projection = map.getProjection();
  const zoom = map.getZoom();
  const center = map.getCenter();
  if (!projection || zoom == null || !center) return null;

  const scale = Math.pow(2, zoom);
  const world = projection.fromLatLngToPoint(latLng);
  const centerWorld = projection.fromLatLngToPoint(center);
  if (!world || !centerWorld) return null;

  const div = map.getDiv();
  return {
    x: (world.x - centerWorld.x) * scale + div.offsetWidth / 2,
    y: (world.y - centerWorld.y) * scale + div.offsetHeight / 2,
  };
};

const snapRadiusMeters = (map) => {
  const zoom = map.getZoom() ?? 7;
  return (40075016 / (256 * Math.pow(2, zoom))) * SNAP_PX;
};

// Componente de desenho de área sobre o mapa. Recebe `map` por prop (padrão
// dos demais filhos do mapa) e opera direto na API bruta do Google Maps.
// `mode`: 'rectangle' (arrasto tipo seleção do Windows) ou 'lasso' (à mão livre).
// Ao finalizar, seleciona todos os pins visíveis dentro da forma, reusando as
// mesmas regras do clique no marker (applyRelocationSelection). Sempre ADICIONA.
const AreaSelector = ({ map, mode }) => {
  const dispatch = useDispatch();
  const store = useStore();

  useEffect(() => {
    if (!map) return;

    // Aplica a seleção a todos os pins visíveis contidos no polígono.
    const selectPinsInPolygon = (polygon) => {
      const { filteredPins } = store.getState().pinsSlice;
      if (!filteredPins?.length) return;
      filteredPins.forEach((pin) => {
        if (!Number.isFinite(pin.lat) || !Number.isFinite(pin.lng)) return;
        const point = new google.maps.LatLng(pin.lat, pin.lng);
        if (google.maps.geometry.poly.containsLocation(point, polygon)) {
          // Relê o state a cada pin: as regras dependem do estado acumulado,
          // então isso torna a seleção em lote equivalente a clicar 1 a 1.
          applyRelocationSelection({
            dataPin: pin,
            state: store.getState(),
            dispatch,
          });
        }
      });
    };

    // Salva as opções que vamos alterar para restaurar no cleanup.
    const prevDraggable = map.get("draggable");
    const prevDraggableCursor = map.get("draggableCursor");
    const prevDisableDblClickZoom = map.get("disableDoubleClickZoom");
    const prevScrollwheel = map.get("scrollwheel");
    map.setOptions({
      draggableCursor: "crosshair",
      // Evita o zoom do duplo-clique enquanto fecha o laço.
      disableDoubleClickZoom: true,
      // Mantém o mapa arrastável para NÃO matar o zoom por scroll (nesta versão
      // do Maps, `draggable: false` desliga também o scroll-zoom). O pan não
      // atrapalha o laço (desenhado por cliques); no retângulo, o pan é
      // desligado só enquanto o botão fica pressionado (ver setupRectangle).
      draggable: true,
      scrollwheel: true,
    });

    const listeners = [];
    let cleanupExtra = () => {};

    if (mode === "rectangle") {
      cleanupExtra = setupRectangle(map, listeners, selectPinsInPolygon);
    } else {
      cleanupExtra = setupLasso(map, listeners, selectPinsInPolygon);
    }

    return () => {
      listeners.forEach((l) => l.remove());
      cleanupExtra();
      map.setOptions({
        draggable: prevDraggable ?? true,
        draggableCursor: prevDraggableCursor ?? "",
        disableDoubleClickZoom: prevDisableDblClickZoom ?? false,
        scrollwheel: prevScrollwheel ?? true,
      });
    };
  }, [map, mode, dispatch, store]);

  return null;
};

// ── Modo retângulo: mousedown → mousemove → mouseup ────────────────────────
const setupRectangle = (map, listeners, onComplete) => {
  const rectangle = new google.maps.Rectangle({
    map,
    strokeColor: STROKE,
    strokeWeight: 2,
    fillColor: STROKE,
    fillOpacity: FILL_OPACITY,
    clickable: false,
    zIndex: FILL_Z_INDEX,
    visible: false,
  });

  let start = null;

  const boundsFrom = (a, b) => {
    const bounds = new google.maps.LatLngBounds();
    bounds.extend(a);
    bounds.extend(b);
    return bounds;
  };

  listeners.push(
    map.addListener("mousedown", (e) => {
      if (!e.latLng) return;
      start = e.latLng;
      // Desliga o pan SÓ durante o arrasto de desenho, senão o mapa faz pan em
      // vez de desenhar o retângulo. Religado no mouseup para preservar o
      // scroll-zoom entre os gestos.
      map.setOptions({ draggable: false });
      rectangle.setBounds(boundsFrom(start, start));
      rectangle.setVisible(true);
    })
  );

  listeners.push(
    map.addListener("mousemove", (e) => {
      if (!start || !e.latLng) return;
      rectangle.setBounds(boundsFrom(start, e.latLng));
    })
  );

  listeners.push(
    map.addListener("mouseup", (e) => {
      // Religa o pan assim que o arrasto de desenho termina (restaura scroll+pan
      // entre gestos). Feito no topo para cobrir todos os caminhos de saída.
      map.setOptions({ draggable: true });
      if (!start || !e.latLng) {
        start = null;
        rectangle.setVisible(false);
        return;
      }
      const bounds = boundsFrom(start, e.latLng);
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      // Ignora cliques sem arrasto (retângulo degenerado).
      if (ne.equals(sw)) {
        start = null;
        rectangle.setVisible(false);
        return;
      }
      const path = [
        { lat: ne.lat(), lng: ne.lng() },
        { lat: ne.lat(), lng: sw.lng() },
        { lat: sw.lat(), lng: sw.lng() },
        { lat: sw.lat(), lng: ne.lng() },
      ];
      const polygon = new google.maps.Polygon({ paths: path });
      onComplete(polygon);
      start = null;
      rectangle.setVisible(false);
    })
  );

  // Rede de segurança: se o botão for solto FORA do mapa, o "mouseup" do Maps
  // não dispara e o pan ficaria preso desligado. Religa via window.
  const windowMouseUp = () => map.setOptions({ draggable: true });
  window.addEventListener("mouseup", windowMouseUp);

  return () => {
    window.removeEventListener("mouseup", windowMouseUp);
    rectangle.setMap(null);
  };
};

// ── Modo laço à mão livre: clicar pontos, fechar perto do 1º ou duplo-clique ─
const setupLasso = (map, listeners, onComplete) => {
  let points = [];

  const pathLine = new google.maps.Polyline({
    map,
    path: [],
    strokeColor: STROKE,
    strokeWeight: 2,
    clickable: false,
    zIndex: LINE_Z_INDEX,
  });

  const previewLine = new google.maps.Polyline({
    map,
    path: [],
    strokeOpacity: 0,
    icons: [
      {
        icon: {
          path: "M 0,-1 0,1",
          strokeOpacity: 0.8,
          strokeColor: STROKE,
          strokeWeight: 2,
          scale: 2,
        },
        offset: "0",
        repeat: "10px",
      },
    ],
    clickable: false,
    zIndex: LINE_Z_INDEX,
  });

  const fillPolygon = new google.maps.Polygon({
    map,
    paths: [],
    fillColor: STROKE,
    fillOpacity: FILL_OPACITY,
    strokeWeight: 0,
    clickable: false,
    zIndex: FILL_Z_INDEX,
  });

  const snapCircle = new google.maps.Circle({
    map,
    center: { lat: 0, lng: 0 },
    radius: 0,
    strokeColor: STROKE,
    strokeWeight: 2,
    fillColor: STROKE,
    fillOpacity: 0.3,
    clickable: false,
    visible: false,
    zIndex: SNAP_Z_INDEX,
  });

  const isNearFirst = (latLng) => {
    if (points.length < 3) return false;
    const fp = toContainerPixel(map, points[0]);
    const cp = toContainerPixel(map, latLng);
    if (!fp || !cp) return false;
    const dx = cp.x - fp.x;
    const dy = cp.y - fp.y;
    return Math.sqrt(dx * dx + dy * dy) < SNAP_PX;
  };

  const reset = () => {
    points = [];
    pathLine.setPath([]);
    previewLine.setPath([]);
    fillPolygon.setPaths([]);
    snapCircle.setVisible(false);
  };

  const finishLasso = () => {
    if (points.length >= 3) {
      const polygon = new google.maps.Polygon({ paths: points });
      onComplete(polygon);
    }
    reset();
  };

  listeners.push(
    map.addListener("click", (e) => {
      if (!e.latLng) return;

      if (points.length >= 3 && isNearFirst(e.latLng)) {
        finishLasso();
        return;
      }

      points.push(e.latLng);
      pathLine.setPath(points);

      if (points.length >= 3) fillPolygon.setPaths(points);

      if (points.length === 1) {
        snapCircle.setCenter(e.latLng.toJSON());
        snapCircle.setRadius(snapRadiusMeters(map));
        snapCircle.setVisible(true);
      }
    })
  );

  listeners.push(
    map.addListener("dblclick", () => {
      if (points.length < 3) return;
      points.pop();
      finishLasso();
    })
  );

  listeners.push(
    map.addListener("mousemove", (e) => {
      if (points.length === 0 || !e.latLng) return;
      const snapping = isNearFirst(e.latLng);
      const target = snapping ? points[0] : e.latLng;

      previewLine.setPath([points[points.length - 1], target]);

      if (points.length >= 2) fillPolygon.setPaths([...points, target]);

      snapCircle.setOptions({
        fillOpacity: snapping ? 0.6 : 0.3,
        strokeWeight: snapping ? 3 : 2,
      });
    })
  );

  listeners.push(
    map.addListener("zoom_changed", () => {
      if (points.length > 0) snapCircle.setRadius(snapRadiusMeters(map));
    })
  );

  const keyListener = (e) => {
    if (e.key === "Escape") reset();
  };
  window.addEventListener("keydown", keyListener);

  return () => {
    window.removeEventListener("keydown", keyListener);
    pathLine.setMap(null);
    previewLine.setMap(null);
    fillPolygon.setMap(null);
    snapCircle.setMap(null);
  };
};

export default AreaSelector;
